import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { AccountLookupField } from '../accounts/AccountLookupField'
import { createLegalEntity } from '../../api/legalEntities'
import { ApiError } from '../../api/problem'

interface TaxIdRow {
  type: string
  value: string
}

/**
 * Named `*Stub` because the surrounding Legal Entities module (list, edit,
 * detail) is not built in this slice — but this form itself is real: a live
 * `POST /legal-entities` with an `Idempotency-Key`, real validation, and a
 * real redirect on success. Reached from the Accounts row kebab's
 * "New legal entity here" — never a dead link.
 */
export function NewLegalEntityStub() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const initialAccountId = searchParams.get('accountId')

  const [account, setAccount] = useState<{ id: string; name: string } | null>(
    initialAccountId ? { id: initialAccountId, name: initialAccountId } : null,
  )
  const [name, setName] = useState('')
  const [jurisdiction, setJurisdiction] = useState('')
  const [taxIds, setTaxIds] = useState<TaxIdRow[]>([{ type: '', value: '' }])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isValid = Boolean(account) && name.trim() !== '' && jurisdiction.trim() !== ''

  async function handleSubmit() {
    if (!isValid || !account || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const taxIdentifiers = taxIds
        .filter((row) => row.type.trim() || row.value.trim())
        .map((row) => ({ type: row.type.trim(), value: row.value.trim() }))
      const { entity } = await createLegalEntity({
        accountId: account.id,
        name: name.trim(),
        jurisdiction: jurisdiction.trim(),
        taxIdentifiers,
      })
      navigate(`/clients/legal-entities?accountId=${entity.accountId}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not create this legal entity.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="landmark" size={20} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-lg)' }}>New legal entity</h1>
      </div>

      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        <AccountLookupField id="le-account" label="Account" value={account} onChange={setAccount} />

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="le-name">
            Name
          </label>
          <input id="le-name" className="iw-input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="le-jurisdiction">
            Jurisdiction
          </label>
          <input
            id="le-jurisdiction"
            className="iw-input"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            required
          />
        </div>

        <div className="iw-field">
          <span className="iw-field-label">Tax identifiers (optional)</span>
          {taxIds.map((row, index) => (
            <div key={index} style={{ display: 'flex', gap: 8 }}>
              <input
                className="iw-input"
                placeholder="Type (e.g. EIN)"
                value={row.type}
                onChange={(e) =>
                  setTaxIds((prev) => prev.map((r, i) => (i === index ? { ...r, type: e.target.value } : r)))
                }
                style={{ flex: 1 }}
              />
              <input
                className="iw-input"
                placeholder="Value"
                value={row.value}
                onChange={(e) =>
                  setTaxIds((prev) => prev.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)))
                }
                style={{ flex: 1 }}
              />
            </div>
          ))}
          <button
            type="button"
            className="iw-btn iw-btn--sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setTaxIds((prev) => [...prev, { type: '', value: '' }])}
          >
            <Icon name="plus" size={12} /> Add another
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Link className="iw-btn" to={initialAccountId ? `/clients/accounts/${initialAccountId}` : '/clients/accounts'}>
            Cancel
          </Link>
          <button type="submit" className="iw-btn iw-btn--primary" disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create legal entity'}
          </button>
        </div>
      </form>
    </div>
  )
}
